import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { BookOpen, Lock, CheckCircle2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    // Using XMLHttpRequest to avoid "request object cannot be cloned" error
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/auth/reset-password`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    
    xhr.onload = function() {
      setLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          setSuccess(true);
          toast.success('Password reset successfully!');
        } else {
          setError(data.detail || 'Failed to reset password');
          toast.error(data.detail || 'Failed to reset password');
        }
      } catch (err) {
        setError('Something went wrong. Please try again.');
        toast.error('Something went wrong. Please try again.');
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      setError('Something went wrong. Please try again.');
      toast.error('Something went wrong. Please try again.');
    };
    
    xhr.send(JSON.stringify({ token, new_password: password }));
  };

  return (
    <div className="min-h-screen flex">
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1639753759816-fd5442a41d1f?q=85&w=800&auto=format&fit=crop')` }}
      >
        <div className="absolute inset-0 bg-[#3A5A40]/40"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>Create New Password</h1>
          <p className="text-xl leading-relaxed">Choose a strong password to secure your account.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAEDCD]">
        <Card className="w-full max-w-md p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-lg rounded-2xl">
          <div className="flex items-center justify-center mb-6">
            <BookOpen className="w-12 h-12 text-[#3A5A40]" strokeWidth={1.5} />
          </div>

          {error && !success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                Reset Link Invalid
              </h2>
              <p className="text-[#588157] mb-6">{error}</p>
              <Link to="/forgot-password">
                <Button className="bg-[#3A5A40] text-white hover:bg-[#344E41]" data-testid="request-new-link-button">
                  Request New Link
                </Button>
              </Link>
            </div>
          ) : success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-[#3A5A40]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-[#3A5A40]" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                Password Reset Complete
              </h2>
              <p className="text-[#588157] mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-[#3A5A40] text-white hover:bg-[#344E41]"
                data-testid="go-to-login-button"
              >
                Go to Login
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-center mb-2 text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
                Reset Password
              </h2>
              <p className="text-center text-[#588157] mb-6">
                Enter your new password below.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password" className="text-[#344E41]">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="Enter new password"
                    data-testid="new-password-input"
                    className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
                  />
                </div>
                
                <div>
                  <Label htmlFor="confirmPassword" className="text-[#344E41]">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                    className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-button"
                  className="w-full bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg"
                >
                  {loading ? (
                    'Resetting...'
                  ) : (
                    <>
                      <Lock className="w-5 h-5 mr-2" strokeWidth={1.5} />
                      Reset Password
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
